import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCapability } from '../../../hooks/use-capability';
import { usePresence } from '../../../hooks/use-presence';
import { useAuthStore } from '../../../stores/auth-store';
import { ConflictAlertCard } from '../../supervisor/components';
import {
  useConflictAlertChain,
  useConflictAlertMcpBlockEvents,
  useConflictAlerts,
} from '../../supervisor/hooks/use-conflict-alerts';
import { SEVERITY_LABELS } from '../../supervisor/labels';
import { CentroAlertasDrawer } from '../../supervisor/telas/centro-alertas/CentroAlertasDrawer';
import { SeverityGroup } from '../../supervisor/telas/centro-alertas/SeverityGroup';
import {
  adaptChainToSteps,
  adaptConflictAlertsToVM,
  adaptMcpBlockEvents,
} from '../../supervisor/telas/centro-alertas/tela7-adapter';

import { deriveForensicKpis } from './forensic-kpis';

import type {
  ConflictAlertVM,
  ConflictSeverityVM,
  SupervisorVariant,
} from '../../supervisor/view-models';
import type React from 'react';

// Rótulos de SLA por severidade (header dos grupos — espelha o mockup; informativo).
const SLA_LABELS: Record<ConflictSeverityVM, string> = {
  critical: '30min',
  high: '2h',
  medium: '24h',
  low: '7d',
};

// KPIs forenses (copy verbatim do mockup B3 conformidade.html — sem scaffolding).
const KPI_HINTS = {
  criticalPending: 'requer ação imediata',
  highPending: 'prazo 7d',
  totalPending: 'aguardando DPO',
  resolved: 'histórico forense preservado',
} as const;

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

function ForensicKpiStrip({ alerts }: { alerts: ConflictAlertVM[] }): React.JSX.Element {
  const k = deriveForensicKpis(alerts);
  const cards: { label: string; value: number; hint: string; danger?: boolean; warn?: boolean }[] =
    [
      {
        label: 'Críticos pendentes',
        value: k.criticalPending,
        hint: KPI_HINTS.criticalPending,
        danger: k.criticalPending > 0,
      },
      {
        label: 'Altos pendentes',
        value: k.highPending,
        hint: KPI_HINTS.highPending,
        warn: k.highPending > 0,
      },
      { label: 'Total pendentes', value: k.totalPending, hint: KPI_HINTS.totalPending },
      { label: 'Resolvidos', value: k.resolved, hint: KPI_HINTS.resolved },
    ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 'var(--space-3)',
        padding: 'var(--space-5) var(--space-5) 0',
      }}
    >
      {cards.map((c) => (
        <div
          key={c.label}
          className="card"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-4)',
          }}
        >
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-3)' }}>{c.label}</div>
          <div
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--fw-bold)',
              color:
                c.danger === true
                  ? 'var(--danger-fg)'
                  : c.warn === true
                    ? 'var(--warning-fg)'
                    : 'var(--fg-1)',
            }}
          >
            {c.value}
          </div>
          <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-3)' }}>{c.hint}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Aba forense de conflitos de interesse do Centro de conformidade (DPO).
 *
 * Lente forense (investigar, NÃO agir) — espelho da Tela 7 do supervisor, mas:
 *   - read-only (DPO não tem `conflict-alert-mitigate`/`accept`/`resolve-or-revoke`):
 *     cards sem ações (`showActions={false}`); drawer em `readOnly` (sem rodapé).
 *   - KPIs derivados da própria lista (DPO não tem `conflict-alert-metrics-read`).
 *   - reusa hooks/adapter/drawer/card/SeverityGroup do supervisor (sem duplicar).
 *
 * O detalhe (`/v2/:id`) NÃO redige para o DPO (tem `audit-events-read-sensitive`);
 * o drawer ainda respeita `partiesRedacted` p/ qualquer caller sem a cap.
 */
export function ConformidadeForensicView(): React.JSX.Element {
  const { data, isLoading, isError } = useConflictAlerts();
  const { data: presence } = usePresence();
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canReadPerson = useCapability('person-read');
  const canReadAudit = useCapability('audit-events-read');
  const navigate = useNavigate();

  const [drawerAlert, setDrawerAlert] = useState<ConflictAlertVM | null>(null);

  const openPerson = canReadPerson
    ? (personId: string): void => navigate(`/pessoas/${personId}`)
    : undefined;
  const openAudit = canReadAudit
    ? (alertId: string): void =>
        navigate(`/admin/audit?entityType=conflict_alert&entityId=${alertId}`)
    : undefined;

  // presence resolve o nome real do User; sem resolução → '' (adapter degrada honesto).
  const nameFor = (userId: string): string =>
    presence?.find((p) => p.userId === userId)?.name ?? '';

  // Enriquecimento lazy do drawer: chain pela ORIGEM (não pelo id próprio) + MCP events.
  const { data: chainData } = useConflictAlertChain(
    drawerAlert?.chain.chainOriginId ?? drawerAlert?.id ?? null,
  );
  const { data: mcpData } = useConflictAlertMcpBlockEvents(drawerAlert?.id ?? null);

  if (isLoading) return <div aria-busy="true">Carregando conflitos de interesse…</div>;
  if (isError) return <div role="alert">Erro ao carregar os conflitos de interesse.</div>;
  if (data === undefined) return <></>;

  const alerts = adaptConflictAlertsToVM(data.data, nameFor);
  const bySev = (sev: ConflictSeverityVM): ConflictAlertVM[] =>
    alerts.filter((a) => a.severity === sev);

  // VM enriquecido do drawer (chain/MCP reais; fallback = VM base).
  const enrichedDrawer: ConflictAlertVM | null =
    drawerAlert === null
      ? null
      : (() => {
          const chain = chainData?.data ?? [];
          const steps = chain.length > 0 ? adaptChainToSteps(chain) : drawerAlert.chain.history;
          const idxInChain = steps.findIndex((s) => s.id === drawerAlert.id);
          const position =
            steps.length === 0
              ? drawerAlert.chain.position
              : idxInChain >= 0
                ? idxInChain + 1
                : steps.length;
          const blocked =
            mcpData?.data !== undefined
              ? adaptMcpBlockEvents(mcpData.data)
              : drawerAlert.impact.blockedMcpToolCalls;
          return {
            ...drawerAlert,
            chain: {
              position,
              total: steps.length > 0 ? steps.length : drawerAlert.chain.total,
              history: steps,
            },
            impact: { ...drawerAlert.impact, blockedMcpToolCalls: blocked },
          };
        })();

  return (
    <>
      <ForensicKpiStrip alerts={alerts} />
      <div className={'tela7-body' + (drawerAlert !== null ? ' tela7-body--drawer-open' : '')}>
        <div>
          {alerts.length === 0 ? (
            <ForensicEmptyState />
          ) : (
            (['critical', 'high', 'medium', 'low'] as ConflictSeverityVM[]).map((sev) => {
              const group = bySev(sev);
              if (group.length === 0) return null;
              return (
                <SeverityGroup
                  key={sev}
                  label={SEVERITY_LABELS[sev].toUpperCase()}
                  sev={sev}
                  alerts={group}
                  sla={SLA_LABELS[sev]}
                  defaultOpen={sev === 'critical' || sev === 'high'}
                  renderCard={(a) => (
                    <ConflictAlertCard
                      key={a.id}
                      alert={a}
                      showActions={false}
                      onOpenDrawer={(vm) => {
                        setDrawerAlert(vm);
                      }}
                    />
                  )}
                />
              );
            })
          )}
        </div>

        {enrichedDrawer !== null && (
          <div className="ca-drawer-inline">
            <CentroAlertasDrawer
              alert={enrichedDrawer}
              variant={variant}
              readOnly
              onClose={() => {
                setDrawerAlert(null);
              }}
              onOpenPerson={openPerson}
              onOpenAudit={openAudit}
            />
          </div>
        )}
      </div>
    </>
  );
}

function ForensicEmptyState(): React.JSX.Element {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px dashed var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-10) var(--space-6)',
        textAlign: 'center',
        color: 'var(--fg-3)',
        margin: 'var(--space-5)',
      }}
    >
      <div style={{ fontSize: 'var(--text-3xl)', marginBottom: 12 }}>✓</div>
      <div
        style={{
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--fg-1)',
        }}
      >
        Nenhum conflito de interesse registrado.
      </div>
    </div>
  );
}
